// Strona pojedynczego produktu
//@ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation, Trans } from "react-i18next";
import { useParams, useLocation } from "react-router-dom";
import classnames from "classnames";
import animateScrollTo from "animated-scroll-to";
import { Grid, Tabs, Tab } from "@mui/material";
import {
  ArrowLeft,
  ArrowRight,
  Download,
  ChevronLeft,
  ChevronRight,
} from "react-bootstrap-icons";
import {
  ButtonBack,
  ButtonNext,
  CarouselProvider,
  DotGroup,
  Slide,
  Slider,
} from "pure-react-carousel";

import { useRWD } from "hooks";
import {
  useGetProduct,
  useGetProductSubelementsTypes,
  useGetProductSubelementItems,
  useGetProductBreadcrumbs,
} from "api";
import { IProductsBreadcrumbsRequest } from "api/types";
import { reduxActions, useDispatch, useSelector } from "store";
import {
  Container,
  Breadcrumbs,
  Loader,
  Collapse,
  Label,
  Link,
} from "components/controls";
import { AddToCartButton, ProductSlider, Search } from "components/containers";
import ProductCard from "components/containers/ProductCard/ProductCard";
import { getProductLabels } from "utils/productLabels";
import logo from "assets/logo.svg";
import { useAppNavigate } from "hooks";
import {
  Details,
  Prices,
  Gallery,
  AttributeList,
  OthersAlsoBought,
} from "pages/Product/components";
import NoMatch from "pages/NoMatch";
import { useGetProductSubelementItemsByType } from "api/endpoints/useGetProductSubelementItemsByType";
import { generateProductPageScheme } from "api/helpers";
import { filterAttributesWithIcons } from "utils/productAttributesWithIcons";

import "pure-react-carousel/dist/react-carousel.es.css";
import styles from "theme/pages/Product/Product.module.scss";
import detailsStyles from "theme/pages/Product/components/Details/Details.module.scss";
import othersAlsoBoughtStyles from "theme/pages/Product/components/OthersAlsoBought/OthersAlsoBought.module.scss";

// Na froncie tylko 3 typy: Karta produktu (wszystko karta_*), Atest, Certyfikat wielorazowości
const PRODUCT_ATTACHMENT_TYPE_KEYS = [
  "karta_produktu",
  "atest",
  "certyfikat_wielorazowosci",
] as const;

const Product = ({
  id,
  h1,
  hasSeoTitle,
}: {
  id?: string;
  h1?: string | null;
  hasSeoTitle?: boolean;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useAppNavigate();
  const { profile } = useSelector((state) => state.auth);

  // Załączniki do pobrania tylko dla zalogowanych klientów - INHB-972
  const isLoggedClient = !!profile && profile.role !== "ROLE_OPEN_PROFILE";

  const { isMobile, isDesktop } = useRWD();

  // referencja do paska nawigacji
  // referencja do sekcji pełny opis
  const fullDescriptionRef = useRef<HTMLDivElement>(null);

  // referencja do sekcji z technicznymi atrybutami
  const technicalAttributesRef = useRef<HTMLDivElement>(null);

  // referencja do sekcji z informacjami o dostawie
  const logisticAttributesRef = useRef<HTMLDivElement>(null);

  // referencja do sekcji z plikami
  const filesRef = useRef<HTMLDivElement>(null);

  // referencja do sekcji z subelementami
  const subelementsRef = useRef<HTMLDivElement>(null);

  // ID produktu (przekształcony na int)
  const { id: paramId } = useParams();
  const productId = useMemo(() => parseInt(paramId || id || ""), [id]);

  // ID kategorii lub fraza wyszukiwania potrzebne jako param w breadcrumbs
  const { state } = useLocation();
  const { categoryId, searchKeywords } = (state ||
    {}) as IProductsBreadcrumbsRequest;

  // Aktualnie wybrany tab (główna nawigacja)
  const [activeTab, setActiveTab] = useState<string | number>("id");

  // Aktualnie wybrany typ subelementu
  const [activeSubelementId, setActiveSubelementId] = useState<number | null>(
    null
  );

  // Lista rozwiniętych sekcji
  const [activeSections, setActiveSections] = useState<string[]>([
    "description_full",
  ]);

  // Pobranie produktu
  const {
    data: productData,
    isLoading: isProductLoading,
    error: productError,
  } = useGetProduct(productId, {
    enabled: !!productId,
    retry: false,
  });

  // Pobranie typów subelementów
  const { data: productSubelementsTypesData } = useGetProductSubelementsTypes(
    productId,
    {
      page: 1,
      limit: 999,
    },
    {
      enabled: !!productId,
      onSuccess: (data) => {
        // Ustawienie pierwszego typ subelementu jako wybrany
        setActiveSubelementId(data.items[0]?.id);
      },
    }
  );

  // Pobranie listy produktów po zmianie/wybraniu typu subelementu
  const {
    data: productSubelementItemsData,
    isLoading: isProductSubelementItemsLoading,
  } = useGetProductSubelementItems(
    productId,
    activeSubelementId || 0,
    {
      page: 1,
      limit: 999,
    },
    {
      enabled: !!productId && !!activeSubelementId,
    }
  );

  // Pobranie listy breadcrumbs'ów danego produktu
  const { data: productBreadcrumbsData } = useGetProductBreadcrumbs(
    productId,
    {
      ...(categoryId ? { categoryId } : {}),
      ...(searchKeywords ? { searchKeywords } : {}),
    },
    {
      enabled: !!productId,
    }
  );

  // Ustawienie breadcrums'ów do wyświetlenia JCZ: w jakim momencie ? Po załadowaniu danych ?
  useEffect(() => {
    dispatch(
      reduxActions.setBreadcrumbs(
        productBreadcrumbsData
          ? productBreadcrumbsData.items.map((item) => ({
              name: item.name,
              path: item.url_link ? `/${item.url_link}` : undefined,
            }))
          : [
              {
                name: "Produkty",
                path: "/products",
              },
              {
                name: productData?.product_basic_full_info?.title || "Produkt",
              },
            ]
      )
    );
  }, [productData, productBreadcrumbsData]);

  // Zwinięcie/Rozwinięcie sekcji
  // Przy rozwinięciu zaznaczenie odpowiedniego tab'a w pasku nawigacji
  const toggleSection = (sectionProperty: string) => {
    activeSections.includes(sectionProperty)
      ? setActiveSections((prevState) =>
          prevState.filter((item) => item !== sectionProperty)
        )
      : (setActiveSections((prevState) => [...prevState, sectionProperty]),
        setActiveTab(sectionProperty));
  };

  // Filtrowanie atrybutów technicznych - usuwamy te z ikonami (wyświetlane w Details)
  const filteredTechnicalAttributes = useMemo(() => {
    if (!productData?.product_technical_attributes_full_info?.attributes)
      return [];
    return filterAttributesWithIcons(
      productData.product_technical_attributes_full_info.attributes
    );
  }, [productData]);

  // Elementy głównej nawigacji
  const navTabs: {
    property: string | number;
    label: string;
    collapse?: boolean;
    tab?: boolean;
  }[] = useMemo(() => {
    const tabs: {
      property: string | number;
      label: string;
      collapse?: boolean;
      tab?: boolean;
    }[] = [
      { property: "id", label: t("Produkt") },
      { property: "description_full", label: t("Pełny opis"), collapse: true },
      ...(productData?.product_bookmarks_full_info?.bookmarks
        ?.filter(
          (bookmark) => bookmark.show_bookmark && bookmark.name !== "Pełny opis"
        )
        .map((bookmark) => ({
          property: bookmark.name,
          label: bookmark.name,
          collapse: bookmark.show_bookmark,
        })) || []),
    ];

    if (filteredTechnicalAttributes.length > 0) {
      tabs.push({
        property: "technical_attributes",
        label: t("Dane techniczne"),
        collapse: true,
      });
    }

    if (
      productData?.product_logistics_attributes_full_info?.attributes?.length
    ) {
      tabs.push({
        property: "logistic_attributes",
        label: t("Dane logistyczne"),
        collapse: true,
      });
    }

    if (productSubelementsTypesData?.items?.length) {
      tabs.push(
        ...productSubelementsTypesData.items.map((subelement) => ({
          property: subelement.id,
          label: subelement.name,
          tab: true,
        }))
      );
    }

    return tabs;
  }, [
    productSubelementsTypesData,
    productData,
    filteredTechnicalAttributes,
    t,
  ]);

  // pobranie referencji do sekcji na podstawie nazwy pola w produkcie
  const getRefElement = (elementName: string) =>
    elementName === "description_full"
      ? fullDescriptionRef
      : elementName === "technical_attributes"
      ? technicalAttributesRef
      : elementName === "logistic_attributes"
      ? logisticAttributesRef
      : elementName === "files"
      ? filesRef
      : elementName === "subelements"
      ? subelementsRef
      : null;

  // funkcja odpowiedzialna za przeskrolowanie strony do odpowiedniej sekcji
  const scrollToElement = (elementName: string) => {
    const element = getRefElement(elementName)?.current;

    element
      ? animateScrollTo(element, {
          elementToScroll:
            document.getElementsByClassName("scrollableContent")[0],
          verticalOffset: -48,
        })
      : animateScrollTo(0, {
          elementToScroll:
            document.getElementsByClassName("scrollableContent")[0],
        });

    setActiveSections([...activeSections, elementName]);
  };

  // Pobranie produktów "Możesz potrzebować" z API
  const { data: youMayNeedProductsData } = useGetProductSubelementItemsByType(
    productId,
    "accessory",
    {
      page: 1,
      limit: 999,
    },
    {
      enabled: !!productId,
    }
  );

  // Pobranie produktów "Zamienniki" z API
  const { data: substituteProductsData } = useGetProductSubelementItemsByType(
    productId,
    "substitute",
    { page: 1, limit: 999 },
    { enabled: !!productId }
  );

  // Pobranie produktów "Inni kupili również" z API
  const { data: suggestionData } = useGetProductSubelementItemsByType(
    productId,
    "suggestion",
    { page: 1, limit: 999 },
    { enabled: !!productId }
  );

  const substituteVisibleSlides = isDesktop ? 2.5 : 1;
  const substituteItemsCount = substituteProductsData?.items?.length || 0;
  const substituteMaxIndex = Math.max(
    0,
    Math.ceil(substituteItemsCount - substituteVisibleSlides)
  );
  const youMayNeedVisibleSlides = isDesktop ? 2.5 : 1;
  const youMayNeedItemsCount = youMayNeedProductsData?.items?.length || 0;
  const youMayNeedMaxIndex = Math.max(
    0,
    Math.ceil(youMayNeedItemsCount - youMayNeedVisibleSlides)
  );
  const othersVisibleSlides = isDesktop ? 4 : 5;
  const othersItemsCount = suggestionData?.items?.length || 0;
  const othersMaxIndex = Math.max(0, othersItemsCount - othersVisibleSlides);

  const [substituteSlideIndex, setSubstituteSlideIndex] = useState(0);
  const [youMayNeedSlideIndex, setYouMayNeedSlideIndex] = useState(0);
  const [othersSlideIndex, setOthersSlideIndex] = useState(0);
  const [canGoSubstitutePrev, setCanGoSubstitutePrev] = useState(false);
  const [canGoSubstituteNext, setCanGoSubstituteNext] = useState(false);
  const [canGoYouMayNeedPrev, setCanGoYouMayNeedPrev] = useState(false);
  const [canGoYouMayNeedNext, setCanGoYouMayNeedNext] = useState(false);
  const [canGoOthersPrev, setCanGoOthersPrev] = useState(false);
  const [canGoOthersNext, setCanGoOthersNext] = useState(false);

  useEffect(() => {
    setSubstituteSlideIndex(0);
  }, [productId, substituteItemsCount]);

  useEffect(() => {
    setCanGoSubstitutePrev(substituteSlideIndex > 0);
    setCanGoSubstituteNext(substituteSlideIndex < substituteMaxIndex);
  }, [substituteSlideIndex, substituteMaxIndex]);

  useEffect(() => {
    setYouMayNeedSlideIndex(0);
  }, [productId, youMayNeedItemsCount]);

  useEffect(() => {
    setCanGoYouMayNeedPrev(youMayNeedSlideIndex > 0);
    setCanGoYouMayNeedNext(youMayNeedSlideIndex < youMayNeedMaxIndex);
  }, [youMayNeedSlideIndex, youMayNeedMaxIndex]);

  useEffect(() => {
    setOthersSlideIndex(0);
  }, [productId, othersItemsCount, isDesktop]);

  useEffect(() => {
    setCanGoOthersPrev(othersSlideIndex > 0);
    setCanGoOthersNext(othersSlideIndex < othersMaxIndex);
  }, [othersSlideIndex, othersMaxIndex]);

  // Generowanie JSON-LD schema dla produktu
  const productPageScheme = useMemo(() => {
    if (!productData) return null;
    const schemes = generateProductPageScheme(productData, window.location.href);
    if (!schemes) return null;

    // Google wymaga offers/review/aggregateRating w typie Product.
    // Gdy cena jest niedostępna (niezalogowany użytkownik, brak cennika),
    // dodajemy minimalny offers bez pola price. Brak ceny oznacza sprzedaż
    // na zamówienie — stąd availability = MadeToOrder.
    const productSchemeIndex = schemes.findIndex((s: any) => s['@type'] === 'Product');
    if (productSchemeIndex !== -1 && !schemes[productSchemeIndex].offers) {
      schemes[productSchemeIndex] = {
        ...schemes[productSchemeIndex],
        offers: {
          '@type': 'Offer',
          url: window.location.href,
          itemCondition: 'https://schema.org/NewCondition',
          availability: 'https://schema.org/MadeToOrder',
          seller: {
            '@type': 'Organization',
            name: 'Intermal',
            url: window.location.origin
          }
        }
      };
    }

    // Normalizujemy nazwę sprzedawcy w wariancie z ceną (offers ze schematu
    // bazowego ma sztywne 'WiseB2B') na nazwę sklepu.
    if (productSchemeIndex !== -1 && schemes[productSchemeIndex].offers?.seller) {
      const scheme = schemes[productSchemeIndex];
      schemes[productSchemeIndex] = {
        ...scheme,
        offers: {
          ...scheme.offers,
          seller: { ...scheme.offers.seller, name: 'Intermal' }
        }
      };
    }

    // Google wymaga niepustego pola "name" w typie Product. Gdy tytuł jest pusty,
    // używamy symbolu/indeksu produktu jako wartości zastępczej.
    if (productSchemeIndex !== -1 && !schemes[productSchemeIndex].name) {
      const productInfo = productData.product_basic_full_info;
      schemes[productSchemeIndex] = {
        ...schemes[productSchemeIndex],
        name:
          productInfo?.title ||
          productInfo?.symbol ||
          productInfo?.index ||
          `Produkt ${productData.id}`
      };
    }

    return schemes;
  }, [productData]);

  // INHB-929: 404 renderowane pod oryginalnym adresem zamiast przekierowania na stronę CMS /pl/404
  // (przekierowanie dawało botom soft 404: HTTP 200 z canonicalem /pl/404 i index, follow).
  if (productError?.httpStatus === 404) {
    return <NoMatch />;
  }

  return (
    <div
      className={classnames(styles.componentWrapper, "StylePath-Pages-Product")}
    >
      {productData && (
        <Helmet>
          {!hasSeoTitle && (
            <title>{productData?.product_basic_full_info?.title}</title>
          )}
          {/* INHB-929: canonical bez parametrów zapytania */}
          <link rel="canonical" href={`${window.location.origin}${window.location.pathname}`} />
          {productPageScheme &&
            productPageScheme.map((scheme, index) => (
              <script key={index} type="application/ld+json">
                {JSON.stringify(scheme)}
              </script>
            ))}
        </Helmet>
      )}


      {/* Mobile header z logo i search - poza Container */}
      {isMobile && (
        <div className={styles.mobileHeader}>
          <button onClick={() => navigate("/")} className={styles.mobileLogo}>
            <img src={logo} alt="logo" />
          </button>
          <div className={styles.mobileSearchWrapper}>
            <Search />
          </div>
        </div>
      )}

      {/* Desktop search wrapper */}
      {!isMobile && (
        <div className={styles.searchWrapper}>
          <Search />
        </div>
      )}

      {/* Breadcrumbs - scrollowalne na mobile */}
      <div className={styles.breadcrumbsWrapper}>
        <Breadcrumbs />
      </div>

      {isProductLoading && <Loader />}

      {productData && (
        <>
          {/* Główna karta produktu - layout zgodny z Figma */}
          <div className={styles.productDetailsWrapper}>
            <Container>
              <Grid container spacing={3}>
                {/* Na mobile: Tytuł i metadata nad zdjęciem */}
                <Grid
                  item
                  md={0}
                  xs={12}
                  sx={{
                    order: { xs: 1, md: 0 },
                    display: { xs: "block", md: "none" },
                  }}
                >
                  <div
                    className={classnames(
                      detailsStyles.productDetails,
                      "StylePath-Pages-Product-components-Details"
                    )}
                  >
                    <div className={detailsStyles.mainContent}>
                      <div className={detailsStyles.titleWrapper}>
                        <h1 className={detailsStyles.title}>
                          {h1 || productData?.product_basic_full_info?.title}
                        </h1>
                      </div>
                      <div className={detailsStyles.metadata}>
                        <div className={detailsStyles.metadataItem}>
                          <span className={detailsStyles.metadataLabel}>
                            <Trans>Kategoria</Trans>:
                          </span>
                          <strong className={detailsStyles.metadataValue}>
                            <Link
                              to={`/${productData?.product_basic_full_info?.category_url}`}
                            >
                              {
                                productData?.product_basic_full_info
                                  ?.category_name
                              }
                            </Link>
                          </strong>
                        </div>
                        <div className={detailsStyles.metadataItem}>
                          <span className={detailsStyles.metadataLabel}>
                            <Trans>INDEX</Trans>:
                          </span>
                          <strong className={detailsStyles.metadataValue}>
                            {productData?.product_basic_full_info?.index}
                          </strong>
                        </div>
                        {productData?.product_basic_full_info
                          ?.producer_name && (
                          <div className={detailsStyles.metadataItem}>
                            <span className={detailsStyles.metadataLabel}>
                              <Trans>Producent</Trans>:
                            </span>
                            <strong
                              className={detailsStyles.metadataValue}
                            >
                              {
                                productData?.product_basic_full_info
                                  ?.producer_name
                              }
                            </strong>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Grid>

                {/* Lewa kolumna: Galeria (md={5} zgodnie z Figma) */}
                <Grid item md={4} xs={12} sx={{ order: { xs: 2, md: 1 } }}>
                  <Gallery
                    images={
                      productData?.product_image_formatted_full_info?.images ||
                      []
                    }
                    labels={productData?.product_basic_full_info?.labels || []}
                  />
                </Grid>

                {/* Na mobile: Ceny między zdjęciem a opisem */}
                <Grid
                  item
                  md={0}
                  xs={12}
                  sx={{
                    order: { xs: 3, md: 0 },
                    display: { xs: "block", md: "none" },
                  }}
                  className={styles.productPricesSection}
                >
                  <Prices product={productData} />
                </Grid>

                {/* Prawa kolumna: Szczegóły i ceny (md={7} zgodnie z Figma) */}
                <Grid item md={8} xs={12} sx={{ order: { xs: 4, md: 2 } }}>
                  <Grid
                    container
                    spacing={3}
                    className={styles.productContentWrapper}
                  >
                    {/* Szczegóły produktu */}
                    <Grid
                      item
                      md={8}
                      xs={12}
                      className={styles.productDetailsSection}
                    >
                      <Details
                        product={productData}
                        scrollToDescription={() => {
                          setActiveTab("description_full");
                          setActiveSections([
                            ...activeSections,
                            "description_full",
                          ]);
                          scrollToElement("description_full");
                        }}
                        hideTitleAndMetadata={isMobile}
                        h1={h1}
                      />
                    </Grid>
                    {/* Ceny i akcje - tylko na desktop */}
                    <Grid
                      item
                      md={4}
                      xs={0}
                      sx={{ display: { xs: "none", md: "block" } }}
                      className={styles.productPricesSection}
                    >
                      <Prices product={productData} />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Container>
          </div>

          {substituteProductsData?.items?.length > 0 && (
            <div
              className={classnames(
                styles.youMayNeedWrapper,
                "substituteWrapper"
              )}
            >
              <Container>
                {isMobile ? (
                  <div
                    className={classnames(
                      othersAlsoBoughtStyles.componentWrapper,
                      "StylePath-Page-Product-components-Substitute"
                    )}
                  >
                    <div className={othersAlsoBoughtStyles.sectionTitle}>
                      <span className={othersAlsoBoughtStyles.sectionTitleText}>
                        <Trans>Zamienniki</Trans>
                      </span>
                    </div>
                    <div className={othersAlsoBoughtStyles.sliderWrapper}>
                      <CarouselProvider
                        naturalSlideWidth={0}
                        naturalSlideHeight={0}
                        totalSlides={substituteProductsData.items.length}
                        visibleSlides={1.18}
                        disableKeyboard
                      >
                        <ButtonBack
                          disabled={true}
                          className={classnames(
                            othersAlsoBoughtStyles.navButton,
                            othersAlsoBoughtStyles.back
                          )}
                        >
                          <ChevronLeft />
                        </ButtonBack>
                        <ButtonNext
                          disabled={true}
                          className={classnames(
                            othersAlsoBoughtStyles.navButton,
                            othersAlsoBoughtStyles.next
                          )}
                        >
                          <ChevronRight />
                        </ButtonNext>
                        <Slider className={othersAlsoBoughtStyles.slider}>
                          {substituteProductsData.items.map((product, index) => (
                            <Slide
                              key={product.id}
                              index={index}
                              className={othersAlsoBoughtStyles.slide}
                            >
                              <div className={othersAlsoBoughtStyles.productWrapper}>
                                <ProductCard product={product} />
                              </div>
                            </Slide>
                          ))}
                        </Slider>
                        <DotGroup className={othersAlsoBoughtStyles.trayBar} />
                      </CarouselProvider>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={styles.sectionTitle}>
                      <span className={styles.sectionTitleText}>
                        <Trans>Zamienniki</Trans>
                      </span>
                      <div className={styles.sectionTitleNavigation}>
                        <button
                          className={classnames(
                            styles.navButton,
                            styles.back,
                            canGoSubstitutePrev ? styles.active : styles.inactive
                          )}
                          onClick={() => {
                            const backButton = document.querySelector(
                              ".substituteWrapper .StylePath-Components-Containers-ProductSlider .carousel__back-button"
                            ) as HTMLButtonElement | null;
                            if (backButton && substituteSlideIndex > 0) {
                              backButton.click();
                              setSubstituteSlideIndex((prev) => Math.max(prev - 1, 0));
                            }
                          }}
                          aria-label="Poprzednie"
                        >
                          <ArrowLeft />
                        </button>
                        <button
                          className={classnames(
                            styles.navButton,
                            styles.next,
                            canGoSubstituteNext ? styles.active : styles.inactive
                          )}
                          onClick={() => {
                            const nextButton = document.querySelector(
                              ".substituteWrapper .StylePath-Components-Containers-ProductSlider .carousel__next-button"
                            ) as HTMLButtonElement | null;
                            if (nextButton && substituteSlideIndex < substituteMaxIndex) {
                              nextButton.click();
                              setSubstituteSlideIndex((prev) =>
                                Math.min(prev + 1, substituteMaxIndex)
                              );
                            }
                          }}
                          aria-label="Następne"
                        >
                          <ArrowRight />
                        </button>
                      </div>
                    </div>
                    <div className={styles.youMayNeedSlider}>
                      <ProductSlider
                        products={substituteProductsData.items}
                        itemsPerSlide={substituteVisibleSlides}
                        isArrowHidden={false}
                      />
                    </div>
                  </>
                )}
              </Container>
            </div>
          )}

          {youMayNeedProductsData?.items?.length > 0 && (
            <div
              className={classnames(
                styles.youMayNeedWrapper,
                "youMayNeedWrapper"
              )}
            >
              <Container>
                {/* Na mobile: użyj tego samego wyglądu co OthersAlsoBought */}
                {isMobile ? (
                  <div
                    className={classnames(
                      othersAlsoBoughtStyles.componentWrapper,
                      "StylePath-Page-Product-components-YouMayNeed"
                    )}
                  >
                    <div className={othersAlsoBoughtStyles.sectionTitle}>
                      <span className={othersAlsoBoughtStyles.sectionTitleText}>
                        <Trans>Możesz potrzebować</Trans>
                      </span>
                    </div>
                    {youMayNeedProductsData.items &&
                      youMayNeedProductsData.items.length > 0 && (
                        <div className={othersAlsoBoughtStyles.sliderWrapper}>
                          <CarouselProvider
                            naturalSlideWidth={0}
                            naturalSlideHeight={0}
                            totalSlides={youMayNeedProductsData.items.length}
                            visibleSlides={1.18}
                            disableKeyboard
                          >
                            <ButtonBack
                              disabled={true}
                              className={classnames(
                                othersAlsoBoughtStyles.navButton,
                                othersAlsoBoughtStyles.back
                              )}
                            >
                              <ChevronLeft />
                            </ButtonBack>
                            <ButtonNext
                              disabled={true}
                              className={classnames(
                                othersAlsoBoughtStyles.navButton,
                                othersAlsoBoughtStyles.next
                              )}
                            >
                              <ChevronRight />
                            </ButtonNext>
                            <Slider className={othersAlsoBoughtStyles.slider}>
                              {youMayNeedProductsData.items.map(
                                (product, index) => (
                                  <Slide
                                    key={product.id}
                                    index={index}
                                    className={othersAlsoBoughtStyles.slide}
                                  >
                                    <div
                                      className={
                                        othersAlsoBoughtStyles.productWrapper
                                      }
                                    >
                                      <ProductCard product={product} />
                                    </div>
                                  </Slide>
                                )
                              )}
                            </Slider>
                            <DotGroup
                              className={othersAlsoBoughtStyles.trayBar}
                            />
                          </CarouselProvider>
                        </div>
                      )}
                  </div>
                ) : (
                  <>
                    {/* Na desktop: oryginalny wygląd z ProductSlider */}
                    <div className={styles.sectionTitle}>
                      <span className={styles.sectionTitleText}>
                        <Trans>Możesz potrzebować</Trans>
                      </span>
                      <div className={styles.sectionTitleNavigation}>
                        <button
                          className={classnames(
                            styles.navButton,
                            styles.back,
                            canGoYouMayNeedPrev ? styles.active : styles.inactive
                          )}
                          onClick={() => {
                            const backButton = document.querySelector(
                              ".youMayNeedWrapper .StylePath-Components-Containers-ProductSlider .carousel__back-button"
                            ) as HTMLButtonElement | null;
                            if (backButton && youMayNeedSlideIndex > 0) {
                              backButton.click();
                              setYouMayNeedSlideIndex((prev) => Math.max(prev - 1, 0));
                            }
                          }}
                          aria-label="Poprzednie"
                        >
                          <ArrowLeft />
                        </button>
                        <button
                          className={classnames(
                            styles.navButton,
                            styles.next,
                            canGoYouMayNeedNext ? styles.active : styles.inactive
                          )}
                          onClick={() => {
                            const nextButton = document.querySelector(
                              ".youMayNeedWrapper .StylePath-Components-Containers-ProductSlider .carousel__next-button"
                            ) as HTMLButtonElement | null;
                            if (nextButton && youMayNeedSlideIndex < youMayNeedMaxIndex) {
                              nextButton.click();
                              setYouMayNeedSlideIndex((prev) =>
                                Math.min(prev + 1, youMayNeedMaxIndex)
                              );
                            }
                          }}
                          aria-label="Następne"
                        >
                          <ArrowRight />
                        </button>
                      </div>
                    </div>
                    <div className={styles.youMayNeedSlider}>
                      <ProductSlider
                        products={youMayNeedProductsData.items}
                        itemsPerSlide={youMayNeedVisibleSlides}
                        isArrowHidden={false}
                      />
                    </div>
                  </>
                )}
              </Container>
            </div>
          )}

          <div className={styles.productDetailsWrapper}>
            <Container>
              <div className={styles.sectionTitle}>
                <span className={styles.sectionTitleText}>
                  <Trans>Szczegóły produktu</Trans>
                </span>
              </div>
              <div className={styles.productExtendedWrapper}>
                {navTabs
                  .filter((item) => item.collapse)
                  .map((item) => {
                    // Dla bookmarks - znajdź odpowiedni bookmark
                    const bookmark =
                      productData?.product_bookmarks_full_info?.bookmarks?.find(
                        (b) => b.name === item.property
                      );

                    return (
                      <div
                        ref={getRefElement(
                          typeof item.property === "string" ? item.property : ""
                        )}
                        key={item.property}
                      >
                        <Collapse
                          open={activeSections.includes(
                            item.property.toString()
                          )}
                          title={item.label}
                          onClick={() =>
                            toggleSection(item.property.toString())
                          }
                        >
                          {item.property === "description_full" &&
                            productData?.product_basic_full_info
                              ?.description && (
                              <div
                                className={styles.fullDescription}
                                dangerouslySetInnerHTML={{
                                  __html:
                                    productData?.product_basic_full_info
                                      ?.description,
                                }}
                              />
                            )}
                          {bookmark && (
                            <div
                              className={styles.fullDescription}
                              dangerouslySetInnerHTML={{
                                __html: bookmark.value || "",
                              }}
                            />
                          )}
                          {item.property === "technical_attributes" && (
                            <AttributeList
                              attributes={filteredTechnicalAttributes}
                            />
                          )}
                          {item.property === "logistic_attributes" && (
                            <AttributeList
                              attributes={
                                productData
                                  ?.product_logistics_attributes_full_info
                                  ?.attributes || []
                              }
                            />
                          )}
                        </Collapse>
                      </div>
                    );
                  })}

                {/* Sekcja "Pobierz" – pliki z product_attachment_full_info, nazwa z label */}
                {isLoggedClient &&
                  productData?.product_attachment_full_info?.files &&
                  productData.product_attachment_full_info.files.length > 0 && (
                  <div ref={filesRef}>
                    <Collapse
                      open={activeSections.includes("files")}
                      title="Pobierz"
                      onClick={() => toggleSection("files")}
                    >
                      <div className={styles.downloadSection}>
                        {productData.product_attachment_full_info.files.map(
                          (file) => (
                            <div
                              key={file.id}
                              className={styles.downloadItem}
                            >
                              <span className={styles.downloadLabel}>
                                {(() => {
                                  const raw =
                                    (file as { type?: string }).type ||
                                    file.label ||
                                    "";
                                  const fallback =
                                    file.label ||
                                    `${file.name}.${file.extension}`;
                                  const isKnownType =
                                    PRODUCT_ATTACHMENT_TYPE_KEYS.includes(
                                      raw as (typeof PRODUCT_ATTACHMENT_TYPE_KEYS)[number]
                                    );
                                  return isKnownType
                                    ? t(
                                        `product_attachment.type.${raw}`,
                                        { defaultValue: fallback, lng: "pl" }
                                      )
                                    : fallback;
                                })()}
                              </span>
                              <a
                                className={styles.downloadButton}
                                href={file.url}
                                download={
                                  file.name && file.extension
                                    ? `${file.name}.${file.extension}`
                                    : undefined
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Pobierz
                                <Download />
                              </a>
                            </div>
                          )
                        )}
                      </div>
                    </Collapse>
                  </div>
                )}
              </div>
            </Container>
          </div>

          {/* Sekcja "Inni kupili również" – dane z /products/{id}/subelements/suggestion */}
          {suggestionData?.items?.length > 0 && (
            <div className={styles.othersAlsoBoughtWrapper}>
              <Container>
                <div className={styles.sectionTitle}>
                  <span className={styles.sectionTitleText}>
                    <Trans>Inni kupili również</Trans>
                  </span>
                  <div className={styles.sectionTitleNavigation}>
                    <button
                      className={classnames(
                        styles.navButton,
                        styles.back,
                        canGoOthersPrev ? styles.active : styles.inactive
                      )}
                      onClick={() => {
                        const backButton = document.querySelector(
                          ".StylePath-Page-Product-components-OthersAlsoBought .carousel__back-button"
                        ) as HTMLButtonElement;
                        if (backButton && othersSlideIndex > 0) {
                          backButton.click();
                          setOthersSlideIndex((prev) => Math.max(prev - 1, 0));
                        }
                      }}
                      aria-label="Poprzednie"
                    >
                      <ArrowLeft />
                    </button>
                    <button
                      className={classnames(
                        styles.navButton,
                        styles.next,
                        canGoOthersNext ? styles.active : styles.inactive
                      )}
                      onClick={() => {
                        const nextButton = document.querySelector(
                          ".StylePath-Page-Product-components-OthersAlsoBought .carousel__next-button"
                        ) as HTMLButtonElement;
                        if (nextButton && othersSlideIndex < othersMaxIndex) {
                          nextButton.click();
                          setOthersSlideIndex((prev) =>
                            Math.min(prev + 1, othersMaxIndex)
                          );
                        }
                      }}
                      aria-label="Następne"
                    >
                      <ArrowRight />
                    </button>
                  </div>
                </div>
                <OthersAlsoBought productId={productId} items={suggestionData.items} />
              </Container>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Product;
